import type { WorkspaceSummaryResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { SupportTarget } from './useSupportStart';
import type { SwitcherWorkspaces } from './useSwitcherWorkspaces';

const itemClass =
  'flex min-h-11 w-full min-w-0 flex-col items-start justify-center rounded-control px-3 py-1.5 text-left';

/** Liste in der weißen Seitenleiste. */
export function SwitcherWorkspaceList({
  list,
  currentId,
  onPick,
  disabled = false,
}: {
  list: SwitcherWorkspaces;
  currentId: string | null;
  onPick: (target: SupportTarget) => void;
  disabled?: boolean;
}) {
  if (list.isLoading) {
    return (
      <p role="status" className="text-ink-soft px-1 text-xs">
        Workspaces werden geladen…
      </p>
    );
  }
  if (list.isError) {
    const reason = list.problem?.status === 0 ? 'keine Verbindung' : list.problem?.title;
    return (
      <div role="alert" className="px-1 text-xs">
        <p className="text-sealwax">
          Die Workspaces konnten nicht geladen werden{reason ? ` (${reason})` : ''}.
        </p>
        <button
          type="button"
          onClick={list.retry}
          className="text-ink hover:bg-card-alt focus-visible:outline-forest rounded-control mt-1 min-h-11 px-2 font-semibold underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2"
        >
          Erneut laden
        </button>
      </div>
    );
  }
  if (list.workspaces.length === 0) {
    return (
      <p className="text-ink-soft px-1 text-xs">
        {list.term
          ? `Kein aktiver Kunden-Workspace passt zu „${list.term}“.`
          : 'Es gibt noch keinen aktiven Kunden-Workspace.'}
      </p>
    );
  }
  return (
    <>
      <ul aria-label="Kunden-Workspaces" className="max-h-60 space-y-0.5 overflow-y-auto">
        {list.workspaces.map((workspace) => (
          <li key={workspace.id}>
            <WorkspaceOption
              workspace={workspace}
              current={workspace.id === currentId}
              onPick={onPick}
              disabled={disabled}
            />
          </li>
        ))}
      </ul>
      {list.more && (
        <p className="text-ink-faint px-1 text-[11px]">Weitere Treffer – Suche eingrenzen.</p>
      )}
    </>
  );
}

function WorkspaceOption({
  workspace,
  current,
  onPick,
  disabled,
}: {
  workspace: WorkspaceSummaryResponse;
  current: boolean;
  onPick: (target: SupportTarget) => void;
  disabled: boolean;
}) {
  if (current) {
    return (
      <div aria-current="true" className={cn(itemClass, 'bg-card-alt')}>
        <span className="text-ink w-full truncate text-[13px]" title={workspace.name}>
          {workspace.name}
        </span>
        <span className="text-forest text-[11px]">Aktuelle Support-Sitzung</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick({ id: workspace.id, name: workspace.name })}
      className={cn(
        itemClass,
        'text-ink hover:bg-card-alt hover:text-ink focus-visible:outline-forest focus-visible:outline focus-visible:outline-2',
      )}
    >
      <span className="w-full truncate text-[13px]" title={workspace.name}>
        {workspace.name}
      </span>
      <span className="text-ink-faint w-full truncate font-mono text-[11px]">{workspace.slug}</span>
    </button>
  );
}
